import { STEP_TYPE_LABELS } from './layoutBuilderCopy'

// Step types in pipeline order; anything else is grouped under 'other', last.
const STEP_ORDER = ['launcher', 'inputConverter', 'fileProcessor', 'canonicalProcessor', 'outputConverter', 'sender']

// A component's file type as people read it: the catalog label of the first application that has
// that file type id, else the raw code (null stays null).
export const fileTypeLabel = (fileTypeId, applications) => {
  if (!fileTypeId) return fileTypeId
  const fileType = (applications || []).flatMap(application => application.fileTypes || []).find(item => item.id === fileTypeId)
  return fileType?.name || fileTypeId
}

const matches = (component, query, applications) =>
  !query || [component.name, component.file_type, fileTypeLabel(component.file_type, applications)].some(value => (value || '').toLowerCase().includes(query))

// Visual Builder components grouped by step type for the picker popover: pipeline order, friendly
// labels, unknown step types under "Other". The set of groups depends only on the data, never on
// the filter (so the tab row cannot change as the user types): a group appears if any component has
// that step type, and its `components` are those matching the (case-insensitive) name or file type
// filter (name, raw file type, or the file type's catalog label), possibly none. Within a group the given order is kept. Returns new arrays and never
// mutates its inputs.
export const componentGroups = (components, filter = '', applications = undefined) => {
  const query = filter.trim().toLowerCase()
  const keyOf = component => STEP_ORDER.includes(component.step_type) ? component.step_type : 'other'
  const all = components || []
  return [...STEP_ORDER, 'other']
    .filter(key => all.some(component => keyOf(component) === key))
    .map(key => ({
      key,
      label: STEP_TYPE_LABELS[key],
      components: all.filter(component => keyOf(component) === key && matches(component, query, applications))
    }))
}

export const OTHER_APPLICATION_ID = '__vb_other__'

// The applications a Visual Builder flow side can pick from, built from the vb catalog entry's
// converters of one step type ('inputConverter' for the source, 'outputConverter' for the
// destination). Each converter's `file_type` is looked up in the applications catalog and the
// converter appears under every application that has that file type; converters whose file type
// maps to no application (or that have none) go under "Other". Shaped like catalog applications:
// `fileTypes` options are valued by the converter's file_type (component_type when it has none)
// and labelled by converter name; converters sharing a file type within an application merge into
// one option with their names joined. Applications are sorted case-insensitively, Other last.
// Returns new objects and never mutates its inputs.
export const converterApplications = (components, applications, stepType) => {
  const groups = new Map()
  const add = (id, name, fileTypeId, converterName) => {
    if (!groups.has(id)) groups.set(id, { id, name, fileTypes: new Map() })
    const { fileTypes } = groups.get(id)
    fileTypes.set(fileTypeId, fileTypes.has(fileTypeId) ? `${fileTypes.get(fileTypeId)}, ${converterName}` : converterName)
  }
  ;(components || []).filter(component => component.step_type === stepType).forEach(converter => {
    const owners = converter.file_type
      ? (applications || []).filter(application => (application.fileTypes || []).some(fileType => fileType.id === converter.file_type))
      : []
    if (owners.length === 0) add(OTHER_APPLICATION_ID, 'Other', converter.file_type || converter.component_type, converter.name)
    owners.forEach(application => add(application.id, application.name, converter.file_type, converter.name))
  })
  const toApplication = group => ({ id: group.id, name: group.name, fileTypes: [...group.fileTypes].map(([id, name]) => ({ id, name })) })
  const sorted = [...groups.values()]
    .filter(group => group.id !== OTHER_APPLICATION_ID)
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  return [...sorted, ...(groups.has(OTHER_APPLICATION_ID) ? [groups.get(OTHER_APPLICATION_ID)] : [])].map(toApplication)
}
